import React, { useState, useEffect, useRef, forwardRef, useCallback } from 'react';
import { Box, Button, Collapse, Icon, Checkbox, Radio, IconButton, TextField, LinearProgress, FormLabel, Link, CircularProgress, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { get, last, set, size, unset, uniqBy } from 'global/lib/lodash';
import arrayToTree from 'array-to-tree';
import Modal from 'global/components/modal';
import { DivisionIcon, CommunityIcon } from 'global/components/PerformIcons';
import { _size } from 'global/utils';
import sendData from 'redux/actions/sendData';
import SnackAlerts from 'global/components/snack-alerts';
import ConfirmModal from 'global/components/confirm';
import { flattenAllRealms, sortAlphaNum, timeGreaterThen, uppercaseFirst } from '../../utils';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { useIntersect } from 'global/hooks/useIntersect';
import LotIcon from '@mui/icons-material/Home';
import CompanyIcon from '@mui/icons-material/Business';
import LocationIcon from '@mui/icons-material/AddLocation';
import { filterFormLabelSx } from 'global/styles/filters';
import { Loader } from 'global/styles';
import { useUser } from '../../hooks/useUser';
import { buildFilters } from '../../utils/filter';
import { Column } from 'global/components/row';
import { keyBy } from '../../lib/lodash';
import { Row } from '../row';
import { Text } from '../text';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import {
  ArchiveButtonRow,
  ArchiveRow,
  ArchiveText,
  BackButton,
  BreadcrumbCurrent,
  ChildCount,
  DigDeeperButton,
  DigDeeperHint,
  EditButtonRow,
  EditRow,
  FilterSelectedWrap,
  FilterToggleButton,
  ItemEditContainer,
  ItemTitle,
  ItemsIndent,
  ListHeader,
  ListWrap,
  RealmBreadcrumbs,
  RealmList,
  RealmListItem,
  RealmListItemButton,
  RealmNameText,
  RealmText,
  RealmTypeIcon,
  SelectControl,
  SelectedSummary,
  TitleRow,
  ToggleButton,
} from './styles';

const TIER_ORDER = ['builder', 'division', 'community', 'lot'];
const COMMUNITY_LOT_BATCH_SIZE = 5;
/** Same window as other cached realm reads (`useCache.reload: 20`). */
const COMMUNITY_LOT_CACHE_MINUTES = 20;
/** Wait for scroll to settle so off-screen rows are not requested. */
const COMMUNITY_LOT_BATCH_DELAY_MS = 100;

const TIER_LABEL = {
  builder: { one: 'Builder', many: 'Builders' },
  division: { one: 'Division', many: 'Divisions' },
  community: { one: 'Community', many: 'Communities' },
  lot: { one: 'Lot', many: 'Lots' },
};

const tierIndex = (tier) => TIER_ORDER.indexOf(tier);
const childTierOf = (tier) => TIER_ORDER[tierIndex(tier) + 1];
const listTierOf = (realm) => (!get(realm, 'id') ? 'builder' : childTierOf(get(realm, 'tier')));
/** Label for the list under a parent tier (builder → "Divisions"). */
const childListLabel = (parentTier) => get(TIER_LABEL, [childTierOf(parentTier), 'many']);
const isTierAllowed = (tier, onlyTiers) => !size(onlyTiers) || onlyTiers.indexOf(tier) > -1;
const onlyTiersAlertMessage = (onlyTiers) => {
  const names = TIER_ORDER.filter((t) => onlyTiers.indexOf(t) > -1).map((t) => get(TIER_LABEL, [t, 'many']));
  return `Only ${names.join(' & ')} can be selected with this scoring criteria.`;
};

const deepestAllowedIndex = (onlyTiers) => {
  const idxs = (onlyTiers || []).map(tierIndex).filter((i) => i >= 0);
  return size(idxs) ? Math.max(...idxs) : -1;
};

/** Nearest ancestor whose tier is in onlyTiers (walk-up). Empty when the current realm is above the allowed tiers. */
const nearestAllowedAncestor = (ancestors, onlyTiers) => [...(ancestors || [])].reverse().find((r) => r && isTierAllowed(r.tier, onlyTiers));

const selectedIdOf = (sel) => Object.keys(sel || {})[0];

/** When the user has one builder (and optionally one division), collapse those crumbs. */
const getSoleCollapse = (topTree) => {
  if (size(topTree) !== 1) return null;
  return {
    builderId: get(topTree, '0.id'),
    divisionId: size(get(topTree, '0.children')) === 1 ? get(topTree, '0.children.0.id') : null,
  };
};

/** Breadcrumb label: root/sole nodes use list labels; otherwise the realm name. */
const hierarchyLabel = (realm, sole) => {
  if (!realm?.id) return TIER_LABEL.builder.many;
  if (sole?.builderId != null && realm.tier === 'builder' && realm.id == sole.builderId) return TIER_LABEL.division.many;
  if (sole?.divisionId != null && realm.tier === 'division' && realm.id == sole.divisionId) return TIER_LABEL.community.many;
  return get(realm, 'name', '');
};

/**
 * If the visible list is deeper than any allowed onlyTiers entry, walk up until
 * the list tier is at or above the deepest allowed tier.
 */
const walkUpToAllowedListTier = (company, hist, onlyTiers) => {
  const maxAllowed = deepestAllowedIndex(onlyTiers);
  if (maxAllowed < 0) return { company, hist };

  let nextCompany = company;
  const nextHist = [...hist];
  while (size(nextHist) > 0) {
    const depth = tierIndex(listTierOf(nextCompany) || get(nextCompany, 'tier'));
    if (depth <= maxAllowed) break;
    nextCompany = nextHist.pop();
  }
  return { company: nextCompany, hist: nextHist };
};

const DeeperActionAffordance = ({ realm, count, isLoaded, isLoading, onVisible, onClick, label, asButton, enableLazyLoad }) => {
  const needsLazyLoad = enableLazyLoad && realm.tier === 'community' && !isLoaded;
  // Viewport root so a row counts only when it is actually on screen, including when the dialog scrolls instead of the list.
  const [setNode, entry] = useIntersect({ root: null, rootMargin: '0px', threshold: 0 });
  const isIntersecting = get(entry, 'isIntersecting', false);

  useEffect(() => {
    if (!needsLazyLoad) return undefined;
    onVisible(realm.id, !!isIntersecting);
    return () => onVisible(realm.id, false);
  }, [needsLazyLoad, isIntersecting, realm.id, onVisible]);

  const countNode = <ChildCount>{isLoading ? <CircularProgress size={14} /> : isLoaded || realm.tier !== 'community' ? count : null}</ChildCount>;

  if (!asButton) {
    return (
      <DigDeeperHint ref={setNode} aria-hidden="true">
        {countNode}
        <ChevronRightIcon fontSize="small" />
      </DigDeeperHint>
    );
  }

  return (
    <DigDeeperButton ref={setNode} edge="end" color="inherit" aria-label={isLoaded ? `View ${count} items under ${label}` : `View items under ${label}`} onClick={onClick} size="large">
      {countNode}
      <ChevronRightIcon />
    </DigDeeperButton>
  );
};

export const buildRealmTree = (realms) => {
  return arrayToTree(realms, { parentProperty: 'parentId' });
};

export const getRealmIcon = (realm, iconClass) => {
  if (!realm) {
    return null;
  }
  const tier = get(realm, 'tier', false);
  if (get(realm, 'id') === -1) {
    return <Icon className={iconClass}>add_box</Icon>;
  }
  return tier === 'division' ? (
    <DivisionIcon className={iconClass} />
  ) : tier === 'community' ? (
    <CommunityIcon className={iconClass} />
  ) : tier === 'lot' ? (
    <LotIcon data-testid="lotIcon" className={iconClass} />
  ) : tier === 'builder' ? (
    <CompanyIcon data-testid="builderIcon" className={iconClass} />
  ) : tier === 'shared' ? (
    <SupervisorAccountIcon data-testid="sharedIcon" className={iconClass} />
  ) : (
    <LocationIcon data-testid="locationIcon" className={iconClass} />
  );
};

export const sortRealmsAlphabetically = (realms) => {
  return realms.sort((a, b) => sortAlphaNum(a, b, 'name'));
};

export let ALLREALMS = {};

/** Walk up from `realm` to the nearest tier allowed by onlyTiers. Uses the picker cache. */
export const nearestAllowedRealm = (realm, onlyTiers) => {
  if (!realm || !get(realm, 'id')) return;
  if (isTierAllowed(get(realm, 'tier'), onlyTiers)) return realm;
  let current = realm;
  const seen = new Set();
  while (current && get(current, 'parentId') != null && !seen.has(String(current.id))) {
    seen.add(String(current.id));
    const pid = get(current, 'parentId');
    current = get(ALLREALMS, pid) || get(ALLREALMS, String(pid));
    if (current && isTierAllowed(get(current, 'tier'), onlyTiers)) return current;
  }
};

export const Access = forwardRef((props, ref) => {
  const { realms, allRealms, editable, singleSelect, canAdd, disableListScroll } = props;
  const { me, userBuilderRealm } = useUser();
  let [selected, setSelected] = useState({});
  let [currentRealm, setCurrentRealm] = useState(props.currentRealm || []);
  let [hist, setHist] = useState([]);
  let [topTree, setTopTree] = useState([]);
  const [currentEdit, setCurrentEdit] = useState();
  const [loading, setLoading] = useState();
  const [alerts, setAlerts] = useState();
  const [confirmModal, setConfirmModal] = useState();
  const [modal, setModal] = useState();
  const [allFlat, setAllFlat] = useState([]);
  const hasUpdated = useRef(false);
  const isLotLoad = useRef(false);

  const singleTierSelect = get(props, 'singleTierSelect', true);

  const open = get(props, 'open', true);
  const meRealm = get(me, 'realm.id');

  const showKey = get(props, 'showKey', false);
  const useCurrentChange = get(props, 'useCurrentChange', true);
  const dontSelectCurrent = get(props, 'dontSelectCurrent', false);

  const realmData = useRetrieveData(
    { url: `/realms?realmPicker=1&clientKey=${showKey ? 1 : 0}` },
    {
      logoutOnUnauth: true,
      build: ['realms'],
      waitFor: allRealms ? null : undefined,
      transformData: (d) => {
        ALLREALMS = keyBy(d, 'id');
        return d;
      },
    }
  );

  const useRealmData = !allRealms;
  const _loading = loading || (useRealmData && realmData.loading);

  const findCurrentRealmInTree = (tree, toFind) => {
    let found;
    let foundArr = [];
    let parent;
    let parentArr = [];

    const search = (arr = [], parents = []) => {
      found = (arr || []).find((r) => r.id == toFind.id);
      parent = parent || (arr || []).find((r) => r.id == toFind.parentId);
      if (parent && !size(parentArr)) {
        parentArr = parents;
      }
      if (!found) {
        arr.find((ar) => {
          found = search(ar.children, [...parents, ar]);
          return found;
        });
      } else {
        foundArr = parents;
      }
      return found;
    };
    search(tree);

    return { found, foundArr, parent, parentArr };
  };

  const runInitial = (bypassCurrentRealm = false) => {
    setLoading(true);

    if (useRealmData && realmData.loading) {
      return;
    }

    const aRealms = sortRealmsAlphabetically(allRealms || flattenAllRealms(realmData.data));
    let tree = buildRealmTree(aRealms);
    if (props.showOnlyUsersBuilder) {
      tree = tree.filter((t) => {
        if (t.id == userBuilderRealm.id) {
          return true;
        }
        return false;
      });
    }
    let topLevel = { children: !!size(topTree) ? topTree : tree };
    let newHist = [topLevel];
    let company = tree.find((t) => t.id === meRealm) || get(tree, '0');
    let setRealm = false;

    const sel = editable ? { ...selected } : props.selected || {};
    (realms || []).map((r) => set(sel, r.id, r));

    const crealm = get(currentRealm, 'children') ? currentRealm : props.currentRealm;
    const hasUp = get(hasUpdated, 'current');
    let notifyOnlyTiers = false;

    if (!bypassCurrentRealm && crealm) {
      const { found, foundArr, parent, parentArr } = findCurrentRealmInTree(tree, crealm);
      if (found) {
        if (found.tier === 'builder' || size(foundArr)) {
          newHist = [...newHist, ...foundArr];
          company = found;

          // Stay on this realm (show its children) when onlyTiers excludes it because
          // it's too high — popping to siblings would drop the back-to-parent hist.
          const foundTooHighOrDeep = size(props.onlyTiers) && !isTierAllowed(found.tier, props.onlyTiers);
          if (!hasUp && !foundTooHighOrDeep && (size(foundArr) > 1 || props.openToSelected) && !props.openToChildren) {
            if (size(get(last(newHist), 'children'))) {
              company = newHist.pop();
            }
          }
        }
        if (!editable && !(props.canOnlySelectUsersBuilder && found.tier === 'builder' && found.id !== userBuilderRealm.id)) {
          if (size(props.onlyTiers) && !isTierAllowed(found.tier, props.onlyTiers)) {
            (foundArr || []).map((fa) => newHist.push(fa));
            setRealm = found;
            newHist = uniqBy(newHist, 'id');
            const allowedParent = nearestAllowedAncestor(foundArr, props.onlyTiers);
            if (allowedParent) {
              set(sel, allowedParent.id, allowedParent);
              notifyOnlyTiers = true;
            }
          } else {
            set(sel, found.id, found);
          }
        }
      } else if (parent) {
        company = parent;
        newHist = [...newHist, ...parentArr];
        isLotLoad.current = true;
        if (size(props.onlyTiers) && !isTierAllowed(crealm.tier, props.onlyTiers)) {
          isLotLoad.current = false;
          const allowedParent = nearestAllowedAncestor([...(parentArr || []), parent], props.onlyTiers);
          if (allowedParent && !editable) {
            set(sel, allowedParent.id, allowedParent);
            notifyOnlyTiers = true;
          }
        }
      }
    }

    const compCheck = () => {
      if (size(get(company, 'children')) === 1) {
        const compIsTheFirstChildSelected = get(sel, get(company, 'children.0.id'));
        if (compIsTheFirstChildSelected) {
          return;
        }

        newHist.push(company);
        company = company.children[0];
        compCheck();
      } else if (size(get(company, 'children')) === 0) {
        company = newHist.pop();
      }
    };

    const hasNewHist = size(get(last(newHist), 'children')) > 0;
    const hasCurrentRealm = !bypassCurrentRealm && !!crealm && size(crealm);
    // After create/edit, keep the user on the current list — don't auto-drill or walk up.
    // Skip sole-child drill when we opened to a specific realm: that push leaves the
    // current view on hist (Back to Divisions, and it takes two clicks to reach Builders).
    if (!hasUp && (useRealmData || props.useNewHist)) {
      if (!hasCurrentRealm && hasNewHist && size(tree) > 1) {
        company = newHist.pop();
      } else if (!setRealm && !props.openToChildren && size(get(company, 'children')) === 1) {
        compCheck();
      }
    }

    if (get(company, 'id') == get(currentRealm, 'id') && !!company) {
      company = {
        ...currentRealm,
        ...company,
        loaded: false,
      };
    }

    // Prefer the navigated "found" realm when onlyTiers excluded it, then walk up
    // if that view's list is deeper than the assessment/template allows.
    let viewRealm = setRealm || company;
    if (!hasUp) {
      const walked = walkUpToAllowedListTier(viewRealm, newHist, props.onlyTiers);
      viewRealm = walked.company;
      newHist = walked.hist;
    }

    while (size(newHist) && get(last(newHist), 'id') != null && String(get(last(newHist), 'id')) === String(get(viewRealm, 'id'))) {
      newHist.pop();
    }

    if (dontSelectCurrent && crealm && crealm.tier == 'builder') {
      unset(sel, get(crealm, 'id'));
    }

    setAllFlat(aRealms);
    setTopTree(tree);
    selected = sel;
    setSelected(sel);
    setHist(newHist);
    setCurrentRealm(open ? viewRealm : undefined);
    setLoading(false);

    if (notifyOnlyTiers) {
      setAlerts({ message: onlyTiersAlertMessage(props.onlyTiers), severity: 'info' });
    }

    // Forms pass `selected` so a walk-up pre-select becomes the field value.
    // Task/nav pickers omit it so opening the list does not auto-submit.
    if (!hasUp && !bypassCurrentRealm && props.selected !== undefined) {
      const nextId = selectedIdOf(sel);
      const prevId = selectedIdOf(props.selected);
      if (nextId && nextId !== prevId) {
        props.onSelectedChange && props.onSelectedChange(sel);
      }
    }
  };

  useEffect(() => {
    if (!!open) {
      runInitial();
    }
  }, [allRealms, realmData.data, realmData.loading, open]);

  const reset = () => {
    hist = [];
    topTree = [];
    currentRealm = [];
    selected = {};
    runInitial(true);
    setCurrentEdit();
  };

  const clearSelection = () => {
    selected = {};
    setSelected({});
    props.onSelectedChange && props.onSelectedChange({});
  };

  set(ref, 'current.reset', reset);
  set(ref, 'current.clearSelection', clearSelection);

  useEffect(() => {
    window.MASTERREALM = realmData;

    return () => {
      unset(window, 'MASTERREALM');
    };
  }, []);

  useEffect(() => {
    set(hasUpdated, 'current', false);
    if (!open && useRealmData) {
      setCurrentRealm();
    }
  }, [open]);

  useEffect(() => {
    if (!useCurrentChange) {
      return;
    }

    if (props.currentRealm && isTierAllowed(props.currentRealm.tier, props.onlyTiers)) {
      setSelected({
        [get(props, 'currentRealm.id')]: get(props, 'currentRealm'),
      });
    } else if (singleSelect && !props.currentRealm) {
      setSelected({});
    } else if (!singleSelect) {
      setSelected({ ...selected });
    }
  }, [props.currentRealm]);

  useEffect(() => {
    if (currentRealm) {
      props.onCurrentRealm && props.onCurrentRealm(!currentRealm.id ? null : currentRealm);
    }
  }, [currentRealm]);

  useEffect(() => {
    props.onEditingChange && props.onEditingChange(!!currentEdit);
  }, [currentEdit]);

  useEffect(() => {
    if (!open) {
      props.onEditingChange && props.onEditingChange(false);
    }
  }, [open]);

  const addNewRowRef = useRef(null);

  useEffect(() => {
    if (!(currentEdit && currentEdit.id === -1)) return;
    requestAnimationFrame(() => {
      addNewRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    });
  }, [currentEdit]);

  const listWrapRef = useRef(null);
  const communityLotsCache = useRef({});
  const lotRequestInflight = useRef({});
  const visibleCommunityIds = useRef(new Set());
  const communityLotBatchTimer = useRef(null);
  const currentRealmRef = useRef(currentRealm);
  const onlyTiersRef = useRef(props.onlyTiers);
  currentRealmRef.current = currentRealm;
  onlyTiersRef.current = props.onlyTiers;
  const [loadingLotIds, setLoadingLotIds] = useState({});

  const getCachedLots = (realmOrId) => {
    const id = typeof realmOrId === 'object' ? get(realmOrId, 'id') : realmOrId;
    if (id == null) return;
    const key = String(id);
    const entry = communityLotsCache.current[key];
    if (!entry) return;
    if (timeGreaterThen(entry.at, COMMUNITY_LOT_CACHE_MINUTES)) {
      delete communityLotsCache.current[key];
      return;
    }
    return entry.lots;
  };

  const invalidateCommunityLots = (communityId) => {
    if (communityId == null) return;
    delete communityLotsCache.current[String(communityId)];
  };

  const withCachedLots = useCallback((realm) => {
    if (!realm) return realm;
    if (realm.tier === 'community') {
      const cached = getCachedLots(realm.id);
      if (cached == null || realm.children === cached) return realm;
      return { ...realm, loaded: true, children: cached };
    }
    if (!size(get(realm, 'children'))) return realm;
    return {
      ...realm,
      children: realm.children.map((child) => {
        const cached = getCachedLots(child.id);
        if (child.tier !== 'community' || cached == null || child.children === cached) return child;
        return { ...child, loaded: true, children: cached };
      }),
    };
  }, []);

  const fetchRealmChildren = async (parentId) => {
    const children = await sendData(
      { url: `/realms` },
      {
        params: {
          ...buildFilters({ parent_id: parentId }),
          clientKey: showKey ? 1 : undefined,
        },
        normalize: { build: ['realms'] },
      }
    );
    return sortRealmsAlphabetically(children || []);
  };

  const scheduleCommunityLotBatchRef = useRef(() => {});

  const loadCommunityLots = useCallback(
    (communityId) => {
      const key = String(communityId);
      const cached = getCachedLots(communityId);
      if (cached != null) return Promise.resolve(cached);
      if (lotRequestInflight.current[key]) return lotRequestInflight.current[key];

      setLoadingLotIds((prev) => ({ ...prev, [key]: true }));
      const request = fetchRealmChildren(communityId)
        .then((lots) => {
          communityLotsCache.current[key] = { lots, at: new Date().toISOString() };
          setCurrentRealm((prev) => {
            if (!prev) return prev;
            // The open community may have no children yet. Always attach lots to that view.
            if (String(prev.id) === key && prev.tier === 'community') {
              return prev.children === lots ? prev : { ...prev, loaded: true, children: lots };
            }
            const children = get(prev, 'children');
            if (!size(children)) return prev;
            let changed = false;
            const nextChildren = children.map((child) => {
              if (String(child.id) !== key) return child;
              changed = true;
              return child.children === lots ? child : { ...child, loaded: true, children: lots };
            });
            return changed ? { ...prev, children: nextChildren } : prev;
          });
          return lots;
        })
        .catch((err) => {
          visibleCommunityIds.current.delete(key);
          setAlerts({ message: err.message, severity: 'error' });
          throw err;
        })
        .finally(() => {
          delete lotRequestInflight.current[key];
          setLoadingLotIds((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
          });
          scheduleCommunityLotBatchRef.current();
        });

      lotRequestInflight.current[key] = request;
      return request;
    },
    [showKey]
  );

  const pumpCommunityLotBatch = useCallback(() => {
    const view = currentRealmRef.current;
    if (get(view, 'tier') !== 'division' || !isTierAllowed('lot', onlyTiersRef.current)) return;

    const order = (get(view, 'children') || []).filter((child) => child.tier === 'community');
    for (let i = 0; i < order.length; i += 1) {
      if (Object.keys(lotRequestInflight.current).length >= COMMUNITY_LOT_BATCH_SIZE) break;
      const child = order[i];
      const key = String(child.id);
      if (!visibleCommunityIds.current.has(key)) continue;
      if (getCachedLots(child.id) != null || lotRequestInflight.current[key]) continue;
      loadCommunityLots(child.id);
    }
  }, [loadCommunityLots]);

  const scheduleCommunityLotBatch = useCallback(() => {
    if (communityLotBatchTimer.current) clearTimeout(communityLotBatchTimer.current);
    communityLotBatchTimer.current = setTimeout(() => {
      communityLotBatchTimer.current = null;
      pumpCommunityLotBatch();
    }, COMMUNITY_LOT_BATCH_DELAY_MS);
  }, [pumpCommunityLotBatch]);
  scheduleCommunityLotBatchRef.current = scheduleCommunityLotBatch;

  const setCommunityLotsVisible = useCallback(
    (communityId, isVisible) => {
      const key = String(communityId);
      if (isVisible) visibleCommunityIds.current.add(key);
      else visibleCommunityIds.current.delete(key);
      scheduleCommunityLotBatch();
    },
    [scheduleCommunityLotBatch]
  );

  useEffect(() => {
    if (get(currentRealm, 'tier') === 'division') return;
    visibleCommunityIds.current.clear();
    if (communityLotBatchTimer.current) {
      clearTimeout(communityLotBatchTimer.current);
      communityLotBatchTimer.current = null;
    }
  }, [currentRealm]);

  useEffect(
    () => () => {
      if (communityLotBatchTimer.current) clearTimeout(communityLotBatchTimer.current);
    },
    []
  );

  useEffect(() => {
    const node = listWrapRef.current;
    if (!node) return;
    const scroller = node.closest('.MuiDialogContent-root');
    if (scroller) scroller.scrollTop = 0;
  }, [get(currentRealm, 'id')]);

  useEffect(() => {
    if (!open || loading || (realmData.loading && useRealmData)) return;
    const id = get(currentRealm, 'id');
    const tier = get(currentRealm, 'tier');
    const lotsAllowed = isTierAllowed('lot', props.onlyTiers);
    if (tier !== 'community' || !lotsAllowed || id == null) return;

    const finishLotOpen = () => {
      if (isLotLoad.current && props.currentRealm && props.openToSelected) {
        const sel = { ...selected };
        set(sel, get(props, 'currentRealm.id'), props.currentRealm);
        setSelected(sel);
        isLotLoad.current = false;
      }
    };

    const cached = getCachedLots(id);
    if (cached != null) {
      // A finished request can land in the cache without being copied onto this view
      // (navigation replaced state after the division list was updated).
      if (get(currentRealm, 'children') !== cached) {
        setCurrentRealm((prev) => {
          if (!prev || String(prev.id) !== String(id)) return prev;
          if (prev.children === cached) return prev;
          return { ...prev, loaded: true, children: cached };
        });
      }
      finishLotOpen();
      return;
    }

    // Opening a community loads that community immediately and does not wait for a batch slot.
    loadCommunityLots(id)
      .then(finishLotOpen)
      .catch(() => {});
  }, [currentRealm, open, loading, realmData.loading, loadCommunityLots]);

  const stopProp = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const navigateToHistIndex = (histIndex) => {
    const _hist = hist.slice(0, histIndex);
    const nextCurrent = hist[histIndex];
    setHist(_hist);
    setCurrentRealm(withCachedLots(nextCurrent || { children: topTree }));
    checkDeselect();
    setCurrentEdit();
  };

  const back = () => {
    if (!size(hist)) return;
    navigateToHistIndex(hist.length - 1);
  };

  const checkDeselect = () => {
    if (props.deselectOnChange) {
      setSelected({});
    }
  };

  const selectChildren = (e, realm) => {
    if (realm.tier === 'lot') {
      return;
    }
    stopProp(e);
    const _hist = [...hist];
    _hist.push(currentRealm);
    setHist(_hist);
    setCurrentRealm(withCachedLots(realm));
    checkDeselect();
    setCurrentEdit();
    // Start this community now, even if it is still waiting on the visible-row batch.
    if (realm.tier === 'community' && isTierAllowed('lot', props.onlyTiers)) {
      loadCommunityLots(realm.id);
    }
  };

  const getBackTargetLabel = () => {
    const prev = hist[hist.length - 1];
    // Root hist entry is the builders list (even when we auto-start under a sole builder).
    if (!get(prev, 'id')) return size(topTree) === 1 ? TIER_LABEL.builder.one : TIER_LABEL.builder.many;
    return childListLabel(prev.tier) || get(prev, 'name', 'Locations');
  };

  const getBreadcrumbs = () => {
    const sole = getSoleCollapse(topTree);

    const raw = [];
    hist.forEach((h, histIndex) => {
      // Hide Builders when the user only has one builder (auto-starts at Divisions).
      if (!get(h, 'id')) {
        if (sole) return;
        raw.push({ key: `root-${histIndex}`, label: TIER_LABEL.builder.many, histIndex });
        return;
      }
      // Hide Divisions when the user only has one division (auto-starts at Communities).
      if (sole?.divisionId != null && h.tier === 'builder' && h.id == sole.builderId) return;
      raw.push({
        key: h.id,
        label: hierarchyLabel(h, sole),
        histIndex,
      });
    });

    if (currentRealm) {
      raw.push({
        key: `current-${currentRealm.id || 'root'}`,
        label: hierarchyLabel(currentRealm, sole),
        current: true,
      });
    }

    const crumbs = [];
    raw.forEach((crumb) => {
      const prev = last(crumbs);
      if (prev && prev.label === crumb.label) {
        if (crumb.current) prev.current = true;
        // Prefer the deeper histIndex so "Divisions" returns to the builder's list, not builders.
        if (crumb.histIndex != null) prev.histIndex = crumb.histIndex;
        return;
      }
      crumbs.push({ ...crumb });
    });

    if (size(crumbs)) {
      last(crumbs).current = true;
    }
    return crumbs;
  };

  const editClick = (e, realm) => {
    stopProp(e);
    setCurrentEdit({
      ...realm,
      value: get(realm, 'name', ''),
    });
  };

  const findParent = (realm) => {
    return allFlat.find((f) => f.id == realm.parentId);
  };

  const findMasterParent = (realm) => {
    let topRealm;
    for (let i = 0; i < hist.length; i++) {
      const item = hist[i];
      if (item.id) {
        topRealm = {
          ...item,
          children: undefined,
        };
        break;
      }
    }
    if (!topRealm) {
      topRealm = {
        ...currentRealm,
        children: undefined,
      };
    }
    return topRealm;
  };

  const findTopDivision = (realm) => {
    let topRealm;
    if (realm.tier === 'division') {
      return realm;
    }

    for (let i = 0; i < hist.length; i++) {
      const item = hist[i];
      if (item.id && item.tier === 'division') {
        topRealm = {
          ...item,
          children: undefined,
        };
        break;
      }
    }
    if (!topRealm && currentRealm.tier === 'division') {
      topRealm = {
        ...currentRealm,
        children: undefined,
      };
    }

    return topRealm;
  };

  const onlySelectSameTier = get(props, 'onlySelectSameTier', true);
  const itemSelect = (realm, e) => {
    stopProp(e);
    realm = {
      ...realm,
      topRealm: findMasterParent(realm),
      topDivision: findTopDivision(realm),
    };
    let sel = { ...selected };
    const isSelected = get(sel, realm.id);
    if (singleSelect) {
      sel = { [realm.id]: realm };
    } else {
      if (isSelected) {
        delete sel[realm.id];
      } else {
        set(sel, realm.id, realm);
        if (onlySelectSameTier) {
          Object.keys(sel).map((k) => {
            const r = sel[k];
            if (r.tier != realm.tier) {
              delete sel[k];
            }
          });
        }
      }
    }

    if (singleTierSelect && !singleSelect) {
      for (let k in sel) {
        const obj = sel[k];
        if (obj.tier !== realm.tier) {
          unset(sel, k);
        }
      }
    }

    setSelected(sel);
    props.onSelectedChange && props.onSelectedChange(sel);
  };

  const currentTierBasedOnCurrentRealm = () => {
    const parentTier = get(currentRealm, 'tier');
    if (get(currentRealm, 'children.0.tier')) {
      return get(currentRealm, 'children.0.tier');
    } else {
      if (parentTier === 'division') {
        return 'community';
      } else if (parentTier === 'community') {
        return 'lot';
      }
    }
  };

  const saveEdit = async () => {
    try {
      const realm = currentEdit;

      if (!realm.value) {
        return alert('Name cannot be blank.');
      }

      setLoading(true);

      let resp;
      if (realm.id === -1) {
        const tier = currentTierBasedOnCurrentRealm();
        resp = await sendData(
          {
            url: `/realms?clientKey=${showKey ? 1 : 0}`,
            method: 'POST',
            data: {
              data: {
                type: 'realms',
                attributes: {
                  user_id: tier !== 'room' ? undefined : me.id,
                  name: realm.value,
                  client_key: realm.clientKey,
                  tier,
                  parent_id: get(currentRealm, 'id'),
                },
              },
            },
          },
          { normalize: { build: ['realms'] } }
        );
      } else {
        resp = await sendData(
          {
            url: `/realms/${realm.id}?clientKey=${showKey ? 1 : 0}`,
            method: 'PATCH',
            data: {
              data: {
                id: realm.id,
                type: 'realms',
                attributes: {
                  name: realm.value,
                  client_key: realm.clientKey,
                },
              },
            },
          },
          { normalize: { build: ['realms'] } }
        );
      }

      const updateMethod = get(props, 'onEditSuccess', () => 0);

      // Set before realmData.update() so runInitial (triggered by the refresh) keeps
      // the user on this list instead of re-applying openToSelected / auto-drill.
      set(hasUpdated, 'current', true);
      const editedTier = realm.id === -1 ? currentTierBasedOnCurrentRealm() : get(realm, 'tier');
      if (editedTier === 'lot') {
        invalidateCommunityLots(get(currentRealm, 'id'));
      }

      await Promise.all([realmData.update(), updateMethod(resp)]);

      if (realm.id === -1) {
        let siblings;
        if (editedTier === 'lot') {
          try {
            siblings = await loadCommunityLots(get(currentRealm, 'id'));
          } catch (err) {
            return;
          }
        } else {
          siblings = await fetchRealmChildren(get(currentRealm, 'id'));
        }
        if (!(siblings || []).some((r) => r.name === realm.value)) {
          setAlerts({
            message: `"${realm.value}" matches a previously archived location, so it won't appear in this list. Please choose a different name.`,
            severity: 'warning',
          });
          return;
        }
      }

      setCurrentEdit();
    } catch (err) {
      const msg = err.message.indexOf('already exists') > -1 ? 'There is already an existing location with that name' : err.message;
      setAlerts({
        message: `Error: ${msg}`,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const updateNameNum = (arr) => {
    let val = '';
    const reN = /[0-9]+/g;
    const reA = /[^a-zA-Z]/g;
    let prevNums = [0];
    // start with the last item in the array as the baseline against which to compare
    const startName = get(arr.at(-1), 'name', '');
    if (Array.isArray(startName.match(reN))) {
      const startNum = startName.match(reN).at(-1);
      const startNumInt = parseInt(startNum, 10);
      const startNameNoNumbers = startName.replace(reA, '').toLowerCase();
      arr.reverse().forEach((r, i) => {
        if (i === 0) {
          val = startName.replace(startNum, (startNumInt + 1).toString().padStart(startNum.length, '0'));
        } else {
          if (i === 1) {
            val = '';
          }
          const thisName = get(r, 'name', '');
          const thisNameNoNumbers = thisName.replace(reA, '').toLowerCase();
          if (startNameNoNumbers === thisNameNoNumbers) {
            // We will use the last number if there are multiple matches
            const thisNameNumbers = thisName.match(reN);
            if (Array.isArray(thisNameNumbers) && thisNameNumbers.at(-1)) {
              const thisNumInt = parseInt(thisNameNumbers.at(-1), 10);
              if (thisNumInt < startNumInt) {
                prevNums.push(thisNumInt);
              }
            }
          }
        }
      });
      if (prevNums.length > 1) {
        prevNums.sort((a, b) => (a > b ? 1 : a < b ? -1 : 0));
        let finalNumInt = startNumInt + 1;
        if (startNumInt - prevNums.at(-1) === prevNums.at(-1) - prevNums.at(-2)) {
          finalNumInt = startNumInt * 2 - prevNums.at(-1);
        }
        val = startName.replace(startNum, finalNumInt.toString().padStart(startNum.length, '0'));
      }
    }
    return val;
  };

  const addNew = () => {
    let val = '';
    const visibleRealmsByName = get(currentRealm, 'children', []);
    if (visibleRealmsByName.length === 1) {
      val = updateNameNum(visibleRealmsByName);
    } else if (visibleRealmsByName.length > 1) {
      // Check if the last visible realm has a sibling realm with matching name and lower number
      // and then default to a new incremented name based on the results
      val = updateNameNum([...visibleRealmsByName]);
      if (!val) {
        // If not, check if the last _created_ realm has one
        const visibleRealmsByCreated = [...visibleRealmsByName].sort((a, b) => get(a, 'id') - get(b, 'id'));
        val = updateNameNum(visibleRealmsByCreated);
      }
    }
    setCurrentEdit({
      id: -1,
      value: val,
    });
  };

  const startMove = () => {
    let selMove;
    const selRealm = get(selected, Object.keys(selected)[0]);
    const parent = findParent(selRealm);
    setModal({
      title: 'Choose Realm to move selected realms to',
      open: true,
      onClose: () => setModal(),
      content: () => (
        <Access singleSelect disableListScroll allRealms={allRealms} onlyTiers={[get(parent, 'tier')]} currentRealm={findParent(findParent(selRealm))} onSelectedChange={(sel) => (selMove = sel)} />
      ),
      buttons: [
        { title: 'Cancel', onClick: () => setModal() },
        {
          title: 'Okay',
          variant: 'contained',
          onClick: () => {
            if (!selMove) {
              alert('No realm was selected!');
            } else {
              archive('move', get(selMove, Object.keys(selMove)[0]));
            }
            setModal();
          },
        },
      ],
    });
  };

  const archive = (type = 'archive', moveTo) => {
    const names = Object.keys(selected)
      .map((k) => get(selected, [k, 'name']))
      .join(', ');
    const content = type === 'move' ? `${names} to ${moveTo.name}` : names;
    setConfirmModal({
      title: 'Confirm',
      content: `Are you sure you want to ${type} ${content}?`,
      onOk: async () => {
        try {
          setLoading(true);
          setConfirmModal();

          if (type === 'archive') {
            await Promise.all(
              Object.keys(selected).map(async (k) => {
                const realm = get(selected, k);
                await sendData({
                  url: `/realms/${realm.id}`,
                  method: 'patch',
                  data: {
                    data: {
                      id: realm.id,
                      type: 'realms',
                      attributes: {
                        active: 0,
                      },
                    },
                  },
                });
              })
            );
          } else if (type === 'merge') {
            const remainingRealm = Object.keys(selected)[0];
            const realms = [];
            Object.keys(selected).map(async (k) => {
              if (k !== remainingRealm) {
                realms.push(k);
              }
            });
            await sendData({
              url: `/realms/${remainingRealm}/merge`,
              method: 'patch',
              data: {
                data: {
                  id: remainingRealm,
                  type: 'realms',
                  attributes: {
                    realms,
                  },
                },
              },
            });
          } else if (type === 'move') {
            await Promise.all(
              Object.keys(selected).map(async (k) => {
                const realm = selected[k];
                await sendData({
                  url: `/realms/${realm.id}`,
                  method: 'patch',
                  data: {
                    data: {
                      id: realm.id,
                      type: 'realms',
                      attributes: {
                        parent_id: get(moveTo, 'id'),
                      },
                    },
                  },
                });
              })
            );
          }
          Object.keys(selected).forEach((k) => {
            const realm = selected[k];
            if (get(realm, 'tier') === 'lot') invalidateCommunityLots(realm.parentId);
          });
          if (type === 'move' && get(moveTo, 'tier') === 'community') {
            invalidateCommunityLots(moveTo.id);
          }
          await realmData.update();
          await (props.onEditSuccess && props.onEditSuccess());
          setAlerts({
            message: type === 'move' ? 'Location(s) moved successfully' : type === 'merge' ? 'Locations merged successfully' : 'Operation completed successfully',
            severity: 'success',
          });
          setSelected({});
          setLoading(false);
          setConfirmModal(false);
        } catch (err) {
          setLoading(false);
          setAlerts({
            message: `Error: ${err.message}`,
            severity: 'error',
          });
        }
      },
      onCancel: () => setConfirmModal(),
    });
  };

  const renderEditRow = (realm) => {
    return (
      <ItemEditContainer onClick={stopProp}>
        <EditRow>
          {getRealmIcon(realm)}
          <Column center={false} fullWidth>
            <TextField
              variant={'outlined'}
              size="small"
              fullWidth={true}
              disabled={loading}
              value={currentEdit.value}
              onChange={(e) => setCurrentEdit({ ...currentEdit, value: e.target.value })}
              autoFocus
            />
            <TextField
              sx={{ mt: 1 }}
              variant={'outlined'}
              size="small"
              fullWidth={true}
              disabled={loading}
              value={currentEdit.clientKey}
              placeholder={'Key ID (optional)'}
              onChange={(e) => setCurrentEdit({ ...currentEdit, clientKey: e.target.value })}
            />
          </Column>
        </EditRow>
        <EditButtonRow>
          <Button onClick={() => setCurrentEdit()} disabled={loading} variant="text">
            Cancel
          </Button>
          <Button onClick={() => saveEdit()} disabled={loading} variant="contained" color="secondary">
            Save
          </Button>
        </EditButtonRow>
      </ItemEditContainer>
    );
  };

  const renderTitle = () => {
    const obj = {};
    Object.keys(selected).forEach((k) => {
      const r = selected[k];
      const tier = get(r, 'tier');
      set(obj, tier, [...get(obj, tier, []), r]);
    });

    if (props.showSelectedText && _size(selected)) {
      const firstKey = Object.keys(selected)[0];
      return <SelectedSummary>{`${get(selected, [firstKey, 'name'])} is selected`}</SelectedSummary>;
    }

    return Object.keys(obj)
      .map((k) => {
        if (k === 'community') {
          return `${size(obj[k])} ${size(obj[k]) > 1 ? 'Communities' : 'Community'}`;
        } else {
          return `${size(obj[k])} ${uppercaseFirst(get(obj[k], [0, 'tier'])) + (size(obj[k]) > 1 ? 's' : '')}`;
        }
      })
      .join(', ');
  };

  const canAddInCurrentContext = () => {
    const currentTier = get(currentRealm, 'tier', 'lot');
    const allowedTiers = props.tiersCanAdd || ['division', 'community'];

    if (!allowedTiers.includes(currentTier)) {
      return false;
    }
    return true;
  };

  const getRealmName = (realm) => {
    const realmname = get(realm, 'name', '');
    if (realm.tier === 'builder' && get(realm, 'id') !== get(me, 'realm.id')) {
      return `${realmname} (client)`;
    }
    return realmname;
  };

  const currentItem = size(hist) > 0 && currentRealm;

  const isNewEdit = currentEdit && currentEdit.id === -1;
  const selSize = _size(selected);
  const showAddNew = (() => {
    if (editable || canAdd) {
      return canAddInCurrentContext();
    } else {
      return false;
    }
  })();

  const onlyTiers = props.onlyTiers || [];
  const tierIsAllowed = (tier) => isTierAllowed(tier, onlyTiers);
  const hasSelectableDescendant = (tier) => {
    const idx = tierIndex(tier);
    return idx > -1 && TIER_ORDER.slice(idx + 1).some(tierIsAllowed);
  };
  const lotsAreSelectable = tierIsAllowed('lot');

  const canGoDeeper = (realm) => {
    if (get(realm, 'tier') === 'lot' || !hasSelectableDescendant(realm.tier)) return false;
    if (realm.tier === 'community') {
      const cached = getCachedLots(realm.id);
      if (realm.loaded || cached) return size(cached || realm.children) > 0 || !!canAdd;
      return true;
    }
    return size(get(realm, 'children')) > 0 || !!canAdd;
  };

  const childCount = (realm) => {
    if (realm.tier === 'community') {
      if (!lotsAreSelectable) return 0;
      const cached = getCachedLots(realm.id);
      if (cached) return size(cached);
    }
    return size(get(realm, 'children', []));
  };

  const isCommunityLotsLoaded = (realm) => {
    if (realm.tier !== 'community' || !lotsAreSelectable) return true;
    return !!(realm.loaded || getCachedLots(realm.id));
  };

  const isSelectable = (realm) => {
    if (props.canOnlySelectUsersBuilder && realm.tier === 'builder' && realm.id !== userBuilderRealm.id) {
      return false;
    }
    return tierIsAllowed(realm.tier);
  };

  const showBack = (() => {
    const canBackOutOfBuilder = get(props, 'canBackOutOfBuilder', true);
    if (size(hist) > 0) {
      if (currentItem && currentItem.tier === 'builder') {
        return canBackOutOfBuilder;
      } else {
        return true;
      }
    } else {
      return false;
    }
  })();

  const shouldShowMove = (() => {
    if (selSize < 1) {
      return false;
    }
    let hasDivOrBuilder;
    for (let k in selected) {
      const tier = get(selected, [k, 'tier']);
      const noTiers = ['builder', 'division'];
      if (noTiers.indexOf(tier) > -1) {
        hasDivOrBuilder = true;
        break;
      }
    }
    return !hasDivOrBuilder;
  })();

  const shouldShowArchive = (() => {
    if (selSize < 1) {
      return false;
    }
    let hasBuilder;
    for (let k in selected) {
      const tier = get(selected, [k, 'tier']);
      const noTiers = ['builder'];
      if (noTiers.indexOf(tier) > -1) {
        hasBuilder = true;
        break;
      }
    }
    return !hasBuilder;
  })();

  const breadcrumbs = getBreadcrumbs();
  const communityLotsLoading = get(currentRealm, 'tier') === 'community' && !!loadingLotIds[String(get(currentRealm, 'id'))];
  const sectionTier = currentTierBasedOnCurrentRealm();
  const sectionReady = sectionTier === 'community' || (sectionTier === 'lot' && get(currentRealm, 'loaded'));
  const showEmptySection = sectionReady && !_loading && !communityLotsLoading && !size(get(currentRealm, 'children'));

  return (
    <ListWrap ref={listWrapRef}>
      {editable && selSize > 0 && (
        <ArchiveRow>
          <ArchiveText>{`${selSize} item${selSize > 1 ? 's' : ''} selected`}</ArchiveText>
          <ArchiveButtonRow>
            {!loading && selSize >= 2 && (
              <Button size="small" disabled={loading} variant="contained" onClick={() => archive('merge')} color="secondary">
                MERGE
              </Button>
            )}
            {!loading && shouldShowMove && (
              <Button size="small" disabled={loading} variant="contained" onClick={startMove} color="secondary">
                MOVE
              </Button>
            )}
            {!loading && shouldShowArchive && (
              <Button size="small" disabled={loading} variant="contained" onClick={() => archive()} color="secondary">
                ARCHIVE
              </Button>
            )}
          </ArchiveButtonRow>
        </ArchiveRow>
      )}
      <ListHeader>
        {!editable && <div className="titleDiv">{renderTitle()}</div>}
        {props.selectTitle || null}
        {!!size(breadcrumbs) && (
          <RealmBreadcrumbs data-testid="realmTitle" separator="-">
            {breadcrumbs.map((crumb) =>
              crumb.current ? (
                <BreadcrumbCurrent key={crumb.key} color="text.primary">
                  {crumb.label}
                </BreadcrumbCurrent>
              ) : (
                <Link key={crumb.key} component="button" type="button" underline="hover" color="inherit" onClick={() => navigateToHistIndex(crumb.histIndex)}>
                  {crumb.label}
                </Link>
              )
            )}
          </RealmBreadcrumbs>
        )}
        {showBack && (
          <BackButton startIcon={<ArrowBackIcon />} onClick={back} variant="text" color="primary">
            {`Back to ${getBackTargetLabel()}`}
          </BackButton>
        )}
      </ListHeader>

      {(_loading || communityLotsLoading) && <LinearProgress />}

      <RealmList loading={_loading} disableListScroll={!!disableListScroll}>
        {get(currentRealm, 'children', []).map((realm, i) => {
          const isEdit = currentEdit && get(currentEdit, 'id') === get(realm, 'id');
          const isSelected = !!get(selected, realm.id);
          const deeper = canGoDeeper(realm);
          const hasKey = get(realm, 'clientKey');
          const selectable = isSelectable(realm);
          const count = childCount(realm);
          const lotsLoaded = isCommunityLotsLoaded(realm);

          if (props.hideNonUserBuilders && realm.tier === 'builder' && realm.id !== userBuilderRealm.id) {
            return null;
          }

          if (isEdit) {
            return (
              <RealmListItem key={`dropRealmsDisplay${get(realm, 'id') ?? i}`} sx={{ height: 'auto' }}>
                {renderEditRow(realm)}
              </RealmListItem>
            );
          }

          const deeperAffordance = deeper ? (
            <DeeperActionAffordance
              realm={realm}
              count={count}
              isLoaded={lotsLoaded}
              isLoading={!!loadingLotIds[String(realm.id)]}
              onVisible={setCommunityLotsVisible}
              onClick={(e) => selectChildren(e, realm)}
              label={getRealmName(realm)}
              asButton={selectable}
              enableLazyLoad={lotsAreSelectable}
            />
          ) : null;

          return (
            <RealmListItem key={`dropRealmsDisplay${get(realm, 'id') ?? i}`} loading={_loading} disablePadding selected={selectable && isSelected}>
              <RealmListItemButton
                selected={selectable && isSelected}
                navigateOnly={!selectable && deeper}
                onClick={(e) => {
                  if (selectable) itemSelect(realm, e);
                  else if (deeper) selectChildren(e, realm);
                }}
              >
                {selectable && (
                  <SelectControl>
                    {singleSelect ? <Radio edge="start" checked={isSelected} tabIndex={-1} disableRipple /> : <Checkbox edge="start" checked={isSelected} tabIndex={-1} disableRipple />}
                  </SelectControl>
                )}
                <RealmTypeIcon>{getRealmIcon(realm)}</RealmTypeIcon>
                {editable && (
                  <IconButton color="secondary" size="small" sx={{ mr: 0.5, flexShrink: 0 }} onClick={(e) => editClick(e, realm)}>
                    <Icon>edit</Icon>
                  </IconButton>
                )}
                <RealmNameText primary={getRealmName(realm)} secondary={showKey && hasKey ? `#${hasKey}` : undefined} />
                {!selectable && deeperAffordance}
              </RealmListItemButton>
              {selectable && deeperAffordance}
            </RealmListItem>
          );
        })}

        {showEmptySection && (
          <RealmListItem sx={{ pointerEvents: 'none' }}>
            <Typography variant="body2" color="text.secondary">
              {`There are no ${TIER_LABEL[sectionTier].many} here`}
            </Typography>
          </RealmListItem>
        )}

        {showAddNew && size(hist) > 0 && (
          <RealmListItem ref={addNewRowRef} key={`${currentTierBasedOnCurrentRealm() || ''}-add`} disablePadding>
            {isNewEdit ? (
              renderEditRow(currentEdit)
            ) : (
              <RealmListItemButton onClick={() => addNew()} navigateOnly>
                <RealmTypeIcon>
                  <Icon>add_box</Icon>
                </RealmTypeIcon>
                <RealmNameText primary={`ADD NEW ${(currentTierBasedOnCurrentRealm() || '').toUpperCase()}`} />
              </RealmListItemButton>
            )}
          </RealmListItem>
        )}
      </RealmList>

      <SnackAlerts alerts={alerts} setAlerts={setAlerts} />
      <ConfirmModal {...confirmModal} />
      <Modal {...modal} />
    </ListWrap>
  );
});

const itemsToMap = (items = []) => {
  const map = {};
  (items || []).filter(Boolean).forEach((item) => set(map, item.id, item));
  return map;
};

const useAccessPickerModal = ({ items, selected, onUpdate, modalTitle = 'SET ACCESS', modalProps, accessProps }) => {
  const [accessOpen, setAccessOpen] = useState(false);
  const [sel, setSel] = useState(() => itemsToMap(items));
  const [isEditing, setIsEditing] = useState(false);
  const accessRef = useRef();

  useEffect(() => {
    setSel(itemsToMap(items));
  }, []);

  useEffect(() => {
    if (!accessOpen) {
      setIsEditing(false);
      return;
    }
    if (selected && typeof selected === 'object') {
      setSel({ ...selected });
    } else {
      setSel(itemsToMap(items));
    }
  }, [accessOpen]);

  const onSelectedChange = (next) => setSel(next || {});

  const clearSelection = () => {
    onSelectedChange({});
    get(accessRef, 'current.clearSelection', () => 0)();
  };

  const applySelection = async () => {
    if (onUpdate) await onUpdate(sel);
    setAccessOpen(false);
  };

  const actionButtons = [
    { title: 'Clear', variant: 'text', disabled: isEditing, onClick: clearSelection },
    { title: 'Apply', disabled: isEditing, onClick: applySelection },
  ];

  const accessNode = <Access ref={accessRef} {...(accessProps || {})} open={accessOpen} disableListScroll onSelectedChange={onSelectedChange} onEditingChange={setIsEditing} />;

  const resolvedModalProps = modalProps
    ? {
        open: accessOpen,
        ...modalProps,
        buttons: actionButtons,
        onClose: async () => {
          if (modalProps.onClose) await modalProps.onClose();
          setAccessOpen(false);
        },
      }
    : {
        open: accessOpen,
        title: modalTitle,
        content: accessNode,
        onClose: () => setAccessOpen(false),
        buttons: actionButtons,
      };

  return { accessOpen, setAccessOpen, sel, resolvedModalProps };
};

const RealmTierTitle = ({ realms }) => {
  const byTier = {};
  (realms || []).filter(Boolean).forEach((r) => set(byTier, r.tier, [...get(byTier, r.tier, []), r]));
  return (
    <>
      {['builder', 'division', 'community']
        .filter((tier) => size(byTier[tier]))
        .map((tier) => {
          const count = size(byTier[tier]);
          const labels = TIER_LABEL[tier];
          return (
            <TitleRow key={tier}>
              {getRealmIcon({ tier })}
              <Box component="span" sx={{ ml: 0.5 }}>{`${count} ${count > 1 ? labels.many : labels.one}`}</Box>
            </TitleRow>
          );
        })}
    </>
  );
};

const DefaultItemRow = ({ item, icon }) => (
  <TitleRow>
    {icon}
    <RealmText>{get(item, 'name', '')}</RealmText>
  </TitleRow>
);

export const Dropitems = (props) => {
  const { items = [], title, type, icon, showModify, alwaysShow, modifyLabel = 'MODIFY', formField = false } = props;
  const [open, setOpen] = useState(false);
  const hasItems = size(items) > 0;

  const { setAccessOpen, sel, resolvedModalProps } = useAccessPickerModal({
    items,
    selected: props.selected,
    onUpdate: props.onUpdate,
    modalTitle: props.modalTitle || 'SET ACCESS',
    modalProps: props.modalProps,
    accessProps: props,
  });

  const renderTitle = () => {
    if (title) return title;
    return <Box component="span" sx={{ ml: 0.5 }}>{`${size(items)} ${type}${size(items) !== 1 ? 's' : ''}`}</Box>;
  };

  if (props.render) {
    return (
      <>
        {props.render({ openModal: setAccessOpen, selected: sel, isOpen: resolvedModalProps.open })}
        <Modal {...resolvedModalProps} />
      </>
    );
  }

  if (!hasItems && !showModify) return null;

  return (
    <>
      <TitleRow sx={formField ? { padding: '0 0 5px' } : undefined}>
        {!!icon && <Box sx={{ p: 1, display: 'inline-flex' }}>{icon}</Box>}
        <ItemTitle variant={formField ? 'body1' : 'body2'}>{renderTitle()}</ItemTitle>
        {hasItems && !alwaysShow && !props.hideToggle && (
          <ToggleButton startIcon={<Icon>{open ? 'arrow_drop_up' : 'arrow_drop_down'}</Icon>} onClick={() => setOpen(!open)}>
            {open ? 'HIDE' : 'SHOW'}
          </ToggleButton>
        )}
        {showModify && <ToggleButton onClick={() => setAccessOpen((v) => !v)}>{modifyLabel}</ToggleButton>}
      </TitleRow>
      <Collapse in={open || alwaysShow}>
        <ItemsIndent>
          {items.map((item, i) => (props.renderItem ? props.renderItem(item, i, { TitleRow, ItemTitle, RealmText }) : <DefaultItemRow key={`dropItems-${item.id ?? i}`} item={item} icon={icon} />))}
        </ItemsIndent>
      </Collapse>
      <Modal {...resolvedModalProps} />
    </>
  );
};

const DropRealms = (props) => {
  const { title, icon, showModify, alwaysShow } = props;
  const [open, setOpen] = useState(false);
  const hideShow = get(props, 'hideShow', false);
  const realms = get(props, 'realms', []);
  const hasRealms = size(realms) > 0;
  const modifyLabel = hasRealms ? 'MODIFY' : 'CHOOSE LOCATIONS';
  const titleNode = title || <RealmTierTitle realms={realms} />;

  const { setAccessOpen, resolvedModalProps } = useAccessPickerModal({
    items: realms,
    onUpdate: props.onUpdate,
    modalTitle: get(props, 'modalTitle', 'SET ACCESS'),
    accessProps: props,
  });

  if (props.showAsFilter) {
    return (
      <>
        <Row spaceBetween dontCenter>
          <FormLabel sx={filterFormLabelSx}>Locations</FormLabel>
          <FilterToggleButton onClick={() => setAccessOpen((v) => !v)}>{modifyLabel}</FilterToggleButton>
        </Row>
        <FilterSelectedWrap>
          {hasRealms && !hideShow && !alwaysShow && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <IconButton color="error" onClick={() => props.onUpdate?.({})} size="small" sx={{ mt: 0.5 }}>
                <Icon>close</Icon>
              </IconButton>
              <Box sx={{ pt: hideShow ? '6px' : 0, pb: hideShow ? '7px' : 0 }}>
                {!!icon && icon}
                {titleNode}
              </Box>
              <ToggleButton startIcon={<Icon>{open ? 'arrow_drop_up' : 'arrow_drop_down'}</Icon>} onClick={() => setOpen(!open)} sx={{ mt: 0.5 }}>
                {open ? 'HIDE' : 'SHOW'}
              </ToggleButton>
            </Box>
          )}
          {size(realms) === 0 && (
            <Text italic sx={{ mt: 0.75 }}>
              Any
            </Text>
          )}
          <Collapse in={open || alwaysShow}>
            <ItemsIndent>
              {realms.map((realm, i) => (
                <DefaultItemRow key={`dropRealmsSelect-${realm.id ?? i}`} item={realm} icon={getRealmIcon(realm)} />
              ))}
            </ItemsIndent>
          </Collapse>
        </FilterSelectedWrap>
        <Modal {...resolvedModalProps} />
      </>
    );
  }

  return (
    <Dropitems
      {...props}
      items={realms}
      title={titleNode}
      modifyLabel={modifyLabel}
      showModify={showModify}
      alwaysShow={alwaysShow}
      hideToggle={hideShow}
      icon={icon}
      modalTitle={get(props, 'modalTitle', 'SET ACCESS')}
    />
  );
};

export default DropRealms;
